// src/utils/permissionUtils.js
// Utilidades de rol y sesión. Los permisos se obtienen del backend.

import { getRoleDisplayName } from '@/config/menuPermissions';
import { cargarPermisosDesdeAPI, limpiarCachePermisos } from '@/api/permisosService';

// ==================== CACHÉ ====================
let currentPermissions = null;
let currentUserRoleId = null;
let permissionsLoaded = false;

// ==================== HELPERS DE SESIÓN ====================

/**
 * Obtiene el usuario actual desde localStorage
 */
export const getCurrentUser = () => {
  try {
    const str = localStorage.getItem('user') || localStorage.getItem('user_data');
    return str ? JSON.parse(str) : null;
  } catch (error) {
    console.error('Error obteniendo usuario:', error);
    return null;
  }
};

/**
 * Obtiene el ID del rol actual
 */
export const getCurrentUserRoleId = () => {
  const user = getCurrentUser();
  if (!user) return null;

  const rawId = user.idRol ?? user.id_rol ?? user.rol?.idRol ?? user.rol?.id_rol;
  if (rawId === undefined || rawId === null) return null;

  const parsed = parseInt(rawId);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Obtiene el nombre del rol actual
 */
export const getCurrentUserRole = () => {
  const user = getCurrentUser();
  if (!user) return null;

  return (
    user.nombreRol ||
    user.rol?.nombre ||
    user.rol ||
    null
  );
};

/**
 * Nombre legible del rol actual
 */
export const getCurrentUserRoleDisplay = () => {
  return getRoleDisplayName(getCurrentUserRole());
};

// ==================== PERMISOS (desde el backend) ====================

/**
 * Carga los permisos del usuario actual desde la API
 */
export const loadUserPermissions = async (forceReload = false) => {
  try {
    const roleId = getCurrentUserRoleId();
    if (!roleId) return null;

    // Super admin y admin → acceso total
    if (roleId === 1 || roleId === 2) {
      currentPermissions = { esAdministrador: true };
      currentUserRoleId = roleId;
      permissionsLoaded = true;
      return currentPermissions;
    }

    if (forceReload || currentUserRoleId !== roleId) {
      limpiarCachePermisos();
      currentUserRoleId = roleId;
      currentPermissions = null;
      permissionsLoaded = false;
    }

    if (currentPermissions && permissionsLoaded) {
      return currentPermissions;
    }

    const permissions = await cargarPermisosDesdeAPI(roleId, forceReload);

    if (permissions && typeof permissions === 'object' && !Array.isArray(permissions)) {
      currentPermissions = permissions;
    } else if (Array.isArray(permissions)) {
      const obj = {};
      permissions.forEach(p => {
        if (typeof p === 'string') obj[p] = true;
        else if (p.nombre_accion) obj[p.nombre_accion] = true;
      });
      currentPermissions = obj;
    } else {
      currentPermissions = {};
    }

    permissionsLoaded = true;
    return currentPermissions;
  } catch (error) {
    console.error('❌ Error cargando permisos:', error);
    return {};
  }
};

/**
 * Verifica si el usuario tiene un permiso (async)
 */
export const hasPermission = async (permissionKey) => {
  try {
    const roleId = getCurrentUserRoleId();
    if (!roleId) return false;

    // Admin → todo
    if (roleId === 1 || roleId === 2) {
      if (permissionKey === 'super_admin_only' && roleId !== 1) return false;
      return true;
    }

    const permissions = await loadUserPermissions();
    if (!permissions) return false;
    if (permissions.esAdministrador) return true;

    return permissions[permissionKey] === true;
  } catch (error) {
    console.error('❌ Error verificando permiso:', error);
    return false;
  }
};

/**
 * Versión sincrónica (usa caché)
 */
export const hasPermissionSync = (permissionKey) => {
  try {
    const roleId = getCurrentUserRoleId();
    if (!roleId) return false;

    if (roleId === 1 || roleId === 2) {
      if (permissionKey === 'super_admin_only' && roleId !== 1) return false;
      return true;
    }

    if (currentPermissions) {
      return currentPermissions[permissionKey] === true;
    }
    return false;
  } catch {
    return false;
  }
};

/**
 * Verifica si tiene al menos uno de los permisos
 */
export const hasAnyPermission = async (permissionKeys) => {
  const roleId = getCurrentUserRoleId();
  if (roleId === 1 || roleId === 2) return true;

  for (const key of permissionKeys) {
    if (await hasPermission(key)) return true;
  }
  return false;
};

/**
 * Versión sync de hasAnyPermission
 */
export const hasAnyPermissionSync = (permissionKeys) => {
  const roleId = getCurrentUserRoleId();
  if (roleId === 1 || roleId === 2) return true;

  return permissionKeys.some(key => hasPermissionSync(key));
};

// ==================== HELPERS DE ROL ====================

export const isSuperAdmin = () => getCurrentUserRoleId() === 1;
export const isAdmin = () => getCurrentUserRoleId() === 2;
export const isAdministrative = () => {
  const id = getCurrentUserRoleId();
  return id === 1 || id === 2;
};

// ==================== RECARGAR / LIMPIAR ====================

export const reloadPermissions = async () => {
  limpiarCachePermisos();
  currentPermissions = null;
  currentUserRoleId = null;
  permissionsLoaded = false;
  return await loadUserPermissions(true);
};

export const clearSession = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('user_data');
  localStorage.removeItem('token');
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('isAuthenticated');
  currentPermissions = null;
  currentUserRoleId = null;
  permissionsLoaded = false;
  limpiarCachePermisos();
};

// ==================== EXPORT DEFAULT ====================

export default {
  getCurrentUser,
  getCurrentUserRole,
  getCurrentUserRoleId,
  getCurrentUserRoleDisplay,
  loadUserPermissions,
  hasPermission,
  hasPermissionSync,
  hasAnyPermission,
  hasAnyPermissionSync,
  isSuperAdmin,
  isAdmin,
  isAdministrative,
  reloadPermissions,
  clearSession
};